import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart3,
  DollarSign,
  Zap,
  FileText,
  Clock,
  RefreshCw,
  TrendingUp,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Cpu,
} from "lucide-react";
import "./GFSUsage.css";

const FORM_TYPES_CONFIG = {
  // Original GFS forms
  aof: { name: "Account Opening Form (AOF)", color: "#3b82f6", bgColor: "#dbeafe" },
  redemption: { name: "Redemption (RP)", color: "#ef4444", bgColor: "#fee2e2" },
  additional_purchase: { name: "Additional Purchase (IUTA/AI)", color: "#06b6d4", bgColor: "#cffafe" },
  // TopUp Order forms
  topup_manulife_prs: { name: "Manulife PRS Form", color: "#8b5cf6", bgColor: "#ede9fe" },
  topup_maybank_booking: { name: "Maybank Booking Report", color: "#f59e0b", bgColor: "#fef3c7" },
  topup_phillip_mutual: { name: "Phillip Mutual Report", color: "#10b981", bgColor: "#d1fae5" },
  topup_ifast_orders: { name: "iFAST Cash Orders", color: "#ec4899", bgColor: "#fce7f3" },
  // TopUp Investment forms
  topup_epf_kwsp: { name: "EPF KWSP 9N", color: "#14b8a6", bgColor: "#ccfbf1" },
};

const GFSUsage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);
  const [v1UsageData, setV1UsageData] = useState(null);
  const [v2UsageData, setV2UsageData] = useState(null);
  const [v1WorkOrders, setV1WorkOrders] = useState([]);
  const [v2WorkOrders, setV2WorkOrders] = useState([]);

  useEffect(() => {
    fetchUsage();
    fetchWorkOrders();
  }, [period]);

  const fetchUsage = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      
      // Fetch V1 and V2 usage separately
      const [v1Res, v2Res] = await Promise.all([
        fetch(`/v1/gfs/api/usage?days=${period}&version=v1`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`/v1/gfs/api/usage?days=${period}&version=v2`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      
      if (v1Res.ok) {
        const data = await v1Res.json();
        setV1UsageData(data);
      }
      if (v2Res.ok) {
        const data = await v2Res.json();
        setV2UsageData(data);
      }
    } catch (err) {
      console.error("Failed to fetch usage:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkOrders = async () => {
    try {
      const token = localStorage.getItem("token");
      
      // Fetch V1 and V2 work orders separately
      const [v1Res, v2Res] = await Promise.all([
        fetch(`/v1/gfs/api/work-orders?limit=10&status=completed&version=v1`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`/v1/gfs/api/work-orders?limit=10&status=completed&version=v2`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      
      if (v1Res.ok) {
        const data = await v1Res.json();
        setV1WorkOrders(data.work_orders || []);
      }
      if (v2Res.ok) {
        const data = await v2Res.json();
        setV2WorkOrders(data.work_orders || []);
      }
    } catch (err) {
      console.error("Failed to fetch work orders:", err);
    }
  };

  const getFormTypeConfig = (formType) => FORM_TYPES_CONFIG[formType] || { name: formType, color: "#6b7280", bgColor: "#f3f4f6" };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(2) + "M";
    if (num >= 1000) return (num / 1000).toFixed(1) + "K";
    return num?.toLocaleString() || "0";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="gfs-usage-loading">
        <RefreshCw size={32} className="spin" />
        <p>Loading usage data...</p>
      </div>
    );
  }

  const v1Summary = v1UsageData?.summary || {};
  const v2Summary = v2UsageData?.summary || {};
  const v1Daily = v1UsageData?.daily || [];
  const v2Daily = v2UsageData?.daily || [];

  // Calculate totals
  const totalRequests = (v1Summary.total_requests || 0) + (v2Summary.total_requests || 0);
  const totalTokens = (v1Summary.total_tokens || 0) + (v2Summary.total_tokens || 0);
  const totalCost = (v1Summary.total_cost || 0) + (v2Summary.total_cost || 0);

  const maxV1DailyRequests = Math.max(...v1Daily.map(d => d.requests || 0), 1);
  const maxV2DailyRequests = Math.max(...v2Daily.map(d => d.requests || 0), 1);

  // Usage Summary Card Component
  const UsageSummaryCard = ({ title, icon: Icon, summary, color, bgColor }) => (
    <div className="gfs-version-usage-card" style={{ borderLeftColor: color }}>
      <div className="gfs-version-usage-header" style={{ backgroundColor: bgColor }}>
        <Icon size={20} style={{ color }} />
        <h4>{title}</h4>
      </div>
      <div className="gfs-version-usage-stats">
        <div className="gfs-version-stat">
          <span className="gfs-version-stat-value">{formatNumber(summary.total_requests)}</span>
          <span className="gfs-version-stat-label">Requests</span>
        </div>
        <div className="gfs-version-stat">
          <span className="gfs-version-stat-value">{formatNumber(summary.total_tokens)}</span>
          <span className="gfs-version-stat-label">Tokens</span>
        </div>
        <div className="gfs-version-stat highlight">
          <span className="gfs-version-stat-value" style={{ color }}>${summary.total_cost?.toFixed(4) || "0.0000"}</span>
          <span className="gfs-version-stat-label">Total Cost</span>
        </div>
        <div className="gfs-version-stat">
          <span className="gfs-version-stat-value">{summary.avg_processing_time?.toFixed(2) || "0"}s</span>
          <span className="gfs-version-stat-label">Avg Time</span>
        </div>
      </div>
      <div className="gfs-version-usage-tokens">
        <span><ArrowUpRight size={12} /> {formatNumber(summary.total_input_tokens)} input</span>
        <span><ArrowDownRight size={12} /> {formatNumber(summary.total_output_tokens)} output</span>
      </div>
    </div>
  );

  // Daily Chart Component
  const DailyChart = ({ title, daily, maxRequests, color }) => (
    <div className="gfs-chart-card">
      <h3><TrendingUp size={18} /> {title} ({period} days)</h3>
      {daily.length > 0 ? (
        <div className="gfs-bar-chart">
          {daily.slice(-14).map((day, idx) => (
            <div key={idx} className="gfs-bar-item">
              <span className="gfs-bar-label">
                {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
              <div className="gfs-bar-track">
                <div 
                  className="gfs-bar-fill" 
                  style={{ width: `${(day.requests / maxRequests) * 100}%`, backgroundColor: color }}
                />
              </div>
              <span className="gfs-bar-value">{day.requests}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="gfs-empty-chart">
          <Calendar size={32} />
          <p>No usage data for this period</p>
        </div>
      )}
    </div>
  );

  // Work Orders Table Component
  const WorkOrdersTable = ({ title, workOrders, color, bgColor, version }) => (
    <div className="gfs-chart-card">
      <h3 style={{ color }}><Zap size={18} /> {title}</h3>
      {workOrders.length > 0 ? (
        <table className="gfs-daily-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Filename</th>
              <th>Form Type</th>
              <th>Input Tokens</th>
              <th>Output Tokens</th>
              <th>Total Tokens</th>
              <th>Cost</th>
              <th>Time</th>
            </tr>
          </thead>
          <tbody>
            {workOrders.map((wo, idx) => {
              const config = getFormTypeConfig(wo.form_type);
              return (
                <tr key={idx} onClick={() => navigate(`/gfs/${version}/work-order/${wo._id}`)} style={{ cursor: 'pointer' }}>
                  <td>{formatDate(wo.created_at)}</td>
                  <td className="gfs-filename-cell" title={wo.filename}>
                    {wo.filename?.substring(0, 25)}{wo.filename?.length > 25 ? '...' : ''}
                  </td>
                  <td>
                    <span className="gfs-form-type-badge-sm" style={{ backgroundColor: config.bgColor, color: config.color }}>
                      {config.name}
                    </span>
                  </td>
                  <td>{wo.input_tokens?.toLocaleString() || '-'}</td>
                  <td>{wo.output_tokens?.toLocaleString() || '-'}</td>
                  <td><strong>{wo.tokens?.toLocaleString() || '-'}</strong></td>
                  <td><strong>${wo.cost?.toFixed(6) || '0.000000'}</strong></td>
                  <td>{wo.processing_time?.toFixed(2) || '-'}s</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <div className="gfs-empty-chart">
          <FileText size={32} />
          <p>No completed extractions yet</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="gfs-usage-page">
      {/* Header */}
      <div className="gfs-usage-header">
        <div className="gfs-usage-header-info">
          <h2><BarChart3 size={24} /> Usage & Cost Tracking</h2>
          <p>Monitor your GFS API usage, tokens, and costs by version</p>
        </div>
        <div className="gfs-period-select">
          <label>Period:</label>
          <select value={period} onChange={(e) => setPeriod(Number(e.target.value))}>
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
      </div>

      {/* Combined Summary Cards */}
      <div className="gfs-usage-summary">
        <div className="gfs-summary-card">
          <div className="gfs-summary-icon requests"><FileText size={24} /></div>
          <div className="gfs-summary-info">
            <div className="gfs-summary-value">{formatNumber(totalRequests)}</div>
            <div className="gfs-summary-label">Total Requests (All)</div>
          </div>
        </div>
        <div className="gfs-summary-card">
          <div className="gfs-summary-icon tokens"><Zap size={24} /></div>
          <div className="gfs-summary-info">
            <div className="gfs-summary-value">{formatNumber(totalTokens)}</div>
            <div className="gfs-summary-label">Total Tokens (All)</div>
          </div>
        </div>
        <div className="gfs-summary-card">
          <div className="gfs-summary-icon cost"><DollarSign size={24} /></div>
          <div className="gfs-summary-info">
            <div className="gfs-summary-value">${totalCost?.toFixed(4) || "0.00"}</div>
            <div className="gfs-summary-label">Total Cost (All)</div>
          </div>
        </div>
      </div>

      {/* V1 and V2 Usage Cards Side by Side */}
      <h3 className="gfs-section-subtitle">
        <DollarSign size={18} />
        Cost Breakdown by Version
      </h3>
      <div className="gfs-version-usage-grid">
        <UsageSummaryCard
          title="V1 Pipeline (Textract + Qwen)"
          icon={Zap}
          summary={v1Summary}
          color="#3b82f6"
          bgColor="#eff6ff"
        />
        <UsageSummaryCard
          title="V2 Pipeline (Tesseract + Qwen VL)"
          icon={Cpu}
          summary={v2Summary}
          color="#8b5cf6"
          bgColor="#f5f3ff"
        />
      </div>

      {/* Daily Charts Side by Side */}
      <div className="gfs-usage-charts-grid">
        <DailyChart
          title="V1 Daily Usage"
          daily={v1Daily}
          maxRequests={maxV1DailyRequests}
          color="#3b82f6"
        />
        <DailyChart
          title="V2 Daily Usage"
          daily={v2Daily}
          maxRequests={maxV2DailyRequests}
          color="#8b5cf6"
        />
      </div>

      {/* V1 Individual Work Orders */}
      <WorkOrdersTable
        title="V1 Extraction Costs & Tokens"
        workOrders={v1WorkOrders}
        color="#3b82f6"
        bgColor="#eff6ff"
        version="v1"
      />

      {/* V2 Individual Work Orders */}
      <WorkOrdersTable
        title="V2 Extraction Costs & Tokens"
        workOrders={v2WorkOrders}
        color="#8b5cf6"
        bgColor="#f5f3ff"
        version="v2"
      />

      {/* Daily Breakdown Tables */}
      <div className="gfs-usage-charts-grid">
        <div className="gfs-chart-card">
          <h3 style={{ color: '#3b82f6' }}><Calendar size={18} /> V1 Daily Breakdown</h3>
          {v1Daily.length > 0 ? (
            <table className="gfs-daily-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Requests</th>
                  <th>Tokens</th>
                  <th>Cost</th>
                </tr>
              </thead>
              <tbody>
                {v1Daily.slice().reverse().slice(0, 10).map((day, idx) => (
                  <tr key={idx}>
                    <td>{new Date(day.date).toLocaleDateString()}</td>
                    <td>{day.requests?.toLocaleString()}</td>
                    <td>{day.tokens?.toLocaleString()}</td>
                    <td>${day.cost?.toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="gfs-empty-chart">
              <Calendar size={32} />
              <p>No V1 daily data available</p>
            </div>
          )}
        </div>

        <div className="gfs-chart-card">
          <h3 style={{ color: '#8b5cf6' }}><Calendar size={18} /> V2 Daily Breakdown</h3>
          {v2Daily.length > 0 ? (
            <table className="gfs-daily-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Requests</th>
                  <th>Tokens</th>
                  <th>Cost</th>
                </tr>
              </thead>
              <tbody>
                {v2Daily.slice().reverse().slice(0, 10).map((day, idx) => (
                  <tr key={idx}>
                    <td>{new Date(day.date).toLocaleDateString()}</td>
                    <td>{day.requests?.toLocaleString()}</td>
                    <td>{day.tokens?.toLocaleString()}</td>
                    <td>${day.cost?.toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="gfs-empty-chart">
              <Calendar size={32} />
              <p>No V2 daily data available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GFSUsage;
